import { useEffect, useState, type FormEvent, type ReactNode } from 'react'

import { updateOrganization } from '../api/organizations'
import { listCities, listProductTypesL0 } from '../api/reference'
import { fetchMe, updateProfile } from '../api/me'
import type { MeResponse } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { ApiError } from '../api/client'
import { BillingSettingsPanel } from '../components/billing/BillingSettingsPanel'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { MessengerSettingsPanel } from '../components/settings/MessengerSettingsPanel'
import { SelectCombobox } from '../components/SelectCombobox'
import { isPivotModeEnabled } from '../pivot/pivotMode'
import { isValidLegalEntityInn } from '../utils/inn'

const SettingsColumn = ({
  title,
  description,
  className,
  children,
}: {
  title: string
  description?: string
  className?: string
  children: ReactNode
}) => (
  <article className={['market-settings-column', className].filter(Boolean).join(' ')}>
    <header className="market-settings-column__head">
      <h2 className="market-settings-column__title">{title}</h2>
      {description ? <p className="market-settings-column__desc">{description}</p> : null}
    </header>
    <div className="market-settings-column__body">{children}</div>
  </article>
)

export const SettingsScreen = () => {
  const pivot = isPivotModeEnabled()
  const { session, organizationName, organizations, switchOrganization, updateProfileInSession } =
    useAuth()
  const [profile, setProfile] = useState<MeResponse | null>(null)
  const [email, setEmail] = useState('')
  const [legalName, setLegalName] = useState('')
  const [tradeName, setTradeName] = useState('')
  const [inn, setInn] = useState('')
  const [city, setCity] = useState('moscow')
  const [productTypeIds, setProductTypeIds] = useState<number[]>([])
  const [cities, setCities] = useState<{ code: string; label: string }[]>([])
  const [supplierTypes, setSupplierTypes] = useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [savingProfile, setSavingProfile] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const currentOrg = profile?.organizations.find((org) => org.id === session?.organizationId)
  const currentOrgLabel =
    organizationName ?? currentOrg?.trade_name ?? currentOrg?.legal_name ?? '—'
  const isBuyerOrg = currentOrg?.org_type === 'buyer'

  useEffect(() => {
    void Promise.all([
      listCities().catch(() => []),
      listProductTypesL0().catch(() => []),
    ]).then(([cityOptions, typeOptions]) => {
      setCities(cityOptions)
      setSupplierTypes(typeOptions)
    })
  }, [])

  useEffect(() => {
    void (async () => {
      try {
        const me = await fetchMe()
        setProfile(me)
        setEmail(me.email ?? session?.email ?? '')
        const org = me.organizations.find((item) => item.id === session?.organizationId)
        if (org) {
          setLegalName(org.legal_name)
          setTradeName(org.trade_name ?? '')
          setInn(org.inn ?? '')
          setCity(org.city ?? 'moscow')
          setProductTypeIds(org.product_type_ids ?? [])
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить профиль')
      } finally {
        setLoading(false)
      }
    })()
  }, [session?.organizationId, session?.email])

  const handleProfileSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSavingProfile(true)
    setError(null)
    setMessage(null)
    try {
      const updated = await updateProfile({ email })
      setProfile(updated)
      updateProfileInSession({ displayName: updated.displayName, email: updated.email })
      setMessage('Профиль сохранён')
    } catch (saveError) {
      if (saveError instanceof ApiError) {
        setError(saveError.message || 'Не удалось сохранить профиль')
      } else {
        setError(saveError instanceof Error ? saveError.message : 'Не удалось сохранить профиль')
      }
    } finally {
      setSavingProfile(false)
    }
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!session || !currentOrg?.can_edit) {
      return
    }
    if (!isValidLegalEntityInn(inn)) {
      setError('ИНН юридического лица должен содержать 10 цифр')
      return
    }
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      await updateOrganization(session.organizationId, {
        legalName: legalName.trim(),
        tradeName: tradeName.trim(),
        inn: inn.trim(),
        city,
        // Тип заведения не редактируем в настройках — сохраняем текущее значение без изменений.
        horecaType: currentOrg.org_type === 'buyer' ? currentOrg.horeca_type ?? null : null,
        productTypeIds: currentOrg.org_type === 'supplier' ? productTypeIds : undefined,
      })
      setMessage('Настройки организации сохранены')
    } catch (saveError) {
      if (saveError instanceof ApiError) {
        setError(saveError.message || 'Не удалось сохранить')
      } else {
        setError(saveError instanceof Error ? saveError.message : 'Не удалось сохранить')
      }
    } finally {
      setSaving(false)
    }
  }

  const toggleProductType = (typeId: number) => {
    setProductTypeIds((current) =>
      current.includes(typeId) ? current.filter((id) => id !== typeId) : [...current, typeId],
    )
  }

  return (
    <section className="market-page market-page--wide market-page--settings">
      <MarketPageHeader
        title="Настройки"
        description={
          pivot ? 'Профиль, организация и команда.' : 'Профиль, организация, команда и уведомления.'
        }
      />

      {loading ? (
        <p className="echat-muted">Загрузка…</p>
      ) : (
        <>
          <div className="market-settings-status-bar" aria-live="polite">
            {message ? <p className="market-settings-status market-success">{message}</p> : null}
            {error ? (
              <p className="echat-error" role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <div className="market-settings-columns">
            <SettingsColumn title="Профиль" description="Корпоративная почта для входа">
              <form className="market-form" onSubmit={(event) => void handleProfileSubmit(event)}>
                <label className="echat-field">
                  <span className="echat-field__label">Корпоративная почта</span>
                  <input
                    className="echat-field__input"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </label>
                <button type="submit" className="echat-btn echat-btn--primary echat-btn--sm" disabled={savingProfile}>
                  {savingProfile ? 'Сохранение…' : 'Сохранить профиль'}
                </button>
              </form>
            </SettingsColumn>

            <SettingsColumn title="Организация" description="Реквизиты и профиль заведения">
              {currentOrg?.can_edit === false ? (
                <div>
                  <p className="echat-panel__text">{currentOrgLabel}</p>
                  <p className="echat-muted">У вас нет прав на редактирование настроек организации.</p>
                </div>
              ) : (
                <form className="market-form" onSubmit={(event) => void handleSubmit(event)}>
                  {organizations.length > 1 ? (
                    <SelectCombobox
                      label="Текущая организация"
                      value={String(session?.organizationId ?? '')}
                      options={organizations.map((org) => ({
                        value: String(org.id),
                        label: org.trade_name ?? org.legal_name,
                      }))}
                      onChange={(next) => void switchOrganization(Number(next))}
                    />
                  ) : null}

                  <label className="echat-field">
                    <span className="echat-field__label">Юридическое название</span>
                    <input
                      className="echat-field__input"
                      value={legalName}
                      required
                      onChange={(event) => setLegalName(event.target.value)}
                    />
                  </label>

                  <label className="echat-field">
                    <span className="echat-field__label">Торговое название</span>
                    <input
                      className="echat-field__input"
                      value={tradeName}
                      required
                      onChange={(event) => setTradeName(event.target.value)}
                    />
                  </label>

                  <label className="echat-field">
                    <span className="echat-field__label">ИНН</span>
                    <input
                      className="echat-field__input"
                      required
                      pattern="[0-9]{10}"
                      inputMode="numeric"
                      autoComplete="off"
                      value={inn}
                      onChange={(event) => setInn(event.target.value)}
                    />
                  </label>

                  <SelectCombobox
                    label="Город"
                    value={city}
                    options={cities.map((option) => ({
                      value: option.code,
                      label: option.label,
                    }))}
                    onChange={setCity}
                    required
                  />

                  {currentOrg?.org_type === 'supplier' && supplierTypes.length > 0 ? (
                    <fieldset className="market-fieldset">
                      <legend className="echat-field__label">Категории товаров</legend>
                      <div className="market-checkbox-grid">
                        {supplierTypes.map((type) => (
                          <label key={type.id} className="market-filters__checkbox">
                            <input
                              type="checkbox"
                              checked={productTypeIds.includes(type.id)}
                              onChange={() => toggleProductType(type.id)}
                            />
                            <span>{type.name}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}

                  <button type="submit" className="echat-btn echat-btn--primary echat-btn--sm" disabled={saving}>
                    {saving ? 'Сохранение…' : 'Сохранить организацию'}
                  </button>
                </form>
              )}
            </SettingsColumn>

            {isBuyerOrg ? (
              <>
                <SettingsColumn title="Команда" description="Сотрудники и приглашения">
                  <BillingSettingsPanel organization={currentOrg} section="team" />
                </SettingsColumn>
                {!pivot ? (
                  <SettingsColumn
                    title="Уведомления"
                    description="Боты и push о статусах заявок"
                    className="market-settings-column--notifications"
                  >
                    <MessengerSettingsPanel embedded />
                  </SettingsColumn>
                ) : null}
              </>
            ) : null}
          </div>
        </>
      )}
    </section>
  )
}
